import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import { useCallback } from 'react';
import { refetchVizData } from '/@services/answer/answer.util';
import { useBachMutationWithoutSession } from '/@services/bach/bach-base';
import {
    AnswerKind,
    AnswerKindEnum,
    recallAnswerKind,
    rememberAnswerKind,
} from '/@services/bach/spotql-answer-kind-store';
import { useApolloClient } from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    AnswerContextType,
    LoadExistingAnswerSessionDocument,
    LoadExistingAnswerSessionMutation,
    LoadExistingAnswerSessionMutationVariables,
} from '../generated/graphql-types';

const logger = create('Bach-Ac-History-Service');

export const useLoadExistingAnswerSession = (
    answerSessionId: string,
    onComplete?: (sessionId: string, answerId: string) => void,
    setVisualizationsLoading = true,
    setPhrasesLoading = true,
    shouldRefetchData = true,
    copyAnswerSession = false,
    // Context for the copied session; only sent when copyAnswerSession is set.
    copyAnswerContext?: AnswerContextType,
) => {
    const client = useApolloClient();

    const [
        _loadAnswerSession,
        { data, loading, error },
    ] = useBachMutationWithoutSession<
        LoadExistingAnswerSessionMutation,
        LoadExistingAnswerSessionMutationVariables
    >(answerSessionId, LoadExistingAnswerSessionDocument, {
        // onComplete is captured in `update` (so delivery is anchored to the
        // cache write and survives a later rejection in the mutate chain —
        // Apollo's onCompleted is skipped when awaitRefetchQueries or another
        // watcher in the same broadcast throws, which left copied answers
        // without a session and got #73197 auto-reverted), but it is invoked
        // from a microtask, outside Apollo's synchronous cache-write
        // transaction. A setState inside the transaction (this callback sets
        // answerSessionId/answerId in the conv-assist container) interleaves
        // with the broadcast's re-render and can drop the effect that
        // triggers the viz data fetch (see docs/standards/frontend-
        // performance-review-guidelines.md §3.8). The microtask runs after
        // the transaction and broadcast finish but before the mutate
        // promise's own .then handlers, so callers still see onComplete
        // before Apollo resolves the mutation.
        update: (_cache, result) => {
            if (!_.isFunction(onComplete)) {
                return;
            }
            const answerId = result.data?.Answer__loadSession?.answer?.id;
            const sessionId = result.data?.Answer__loadSession?.id?.sessionId;
            queueMicrotask(() => {
                if (sessionId) {
                    onComplete(sessionId, answerId);
                } else {
                    // Never skip silently: downstream rendering is gated on
                    // onComplete seeding the session, so a missing sessionId
                    // must be loud.
                    logger.error(
                        'Answer__loadSession completed without a sessionId; onComplete skipped',
                        { answerSessionId },
                    );
                }
            });
        },
        shouldRefetchData,
        ...(shouldRefetchData
            ? {
                  customRefetchQueriesFn: (_, result) => {
                      return refetchVizData(client, result);
                  },
              }
            : {
                  refetchQueries: result => {
                      return refetchVizData(client, result);
                  },
              }),
        awaitRefetchQueries: true,
        setVisualizationsLoading,
        setPhrasesLoading,
    });

    // Memoized so effects can safely list it in their dependency arrays
    // without re-firing on every render.
    const loadAnswerSession = useCallback(
        (
            bachSessionId: string,
            bachGenNo: number,
            acSessionId: string,
            acGenNo: number,
            bachGenNoWorkingSet?: number[],
            acGenNoWorkingSet?: number[],
            answerKind?: AnswerKind,
        ) => {
            const resolvedAnswerKind =
                answerKind ?? recallAnswerKind(bachSessionId);
            rememberAnswerKind(bachSessionId, resolvedAnswerKind);
            const isSpotql = resolvedAnswerKind === AnswerKindEnum.Spotql;
            const shouldSendAcSession = !isSpotql && !!acSessionId;
            // Working sets are optional; omit the keys entirely when absent
            // so the variables object carries no undefined values (identical
            // on the wire, deterministic for mock-based tests).
            const variables: LoadExistingAnswerSessionMutationVariables = {
                session: {
                    sessionId: bachSessionId,
                    genNo: bachGenNo,
                    ...(bachGenNoWorkingSet
                        ? { genNoWorkingSet: bachGenNoWorkingSet }
                        : {}),
                    ...(shouldSendAcSession
                        ? {
                              acSession: {
                                  sessionId: acSessionId,
                                  genNo: acGenNo,
                                  ...(acGenNoWorkingSet
                                      ? { genNoWorkingSet: acGenNoWorkingSet }
                                      : {}),
                              },
                          }
                        : {}),
                    ...(resolvedAnswerKind
                        ? { answerKind: resolvedAnswerKind }
                        : {}),
                },
            };
            if (copyAnswerSession) {
                variables.copy = copyAnswerSession;
                if (copyAnswerContext) {
                    variables.answerContext = copyAnswerContext;
                }
            }
            return _loadAnswerSession({
                variables: {
                    ...variables,
                },
            });
        },
        [_loadAnswerSession, copyAnswerSession, copyAnswerContext],
    );

    return {
        loadAnswerSession,
        data: data?.Answer__loadSession.answer,
        loading,
        error,
    };
};
